import { useState, useEffect, useRef, useCallback } from "react";
import { cn } from "../utils/cn";

/**
 * Data for a single toast in the stack.
 */
export interface UndoToastItem {
  /** Unique identifier for this toast */
  id: string;
  /** Name of the deleted scenario */
  scenarioName: string;
  /** Timestamp when the toast was created (Date.now()) */
  startedAt: number;
}

export interface UndoToastProps {
  /** Unique identifier for this toast */
  id: string;
  /** Name of the deleted scenario */
  scenarioName: string;
  /** Duration in milliseconds before expiration (default: 5000) */
  duration?: number;
  /** Called when the user clicks Undo */
  onUndo: (id: string) => void;
  /** Called when the countdown expires */
  onExpire: (id: string) => void;
  /** Called when the user dismisses the toast (confirms deletion) */
  onDismiss: (id: string) => void;
}

/**
 * UndoToast — a timed toast allowing the user to undo a scenario deletion.
 *
 * Shows the deleted scenario name, a prominent "Undo" button, a dismiss (×) button,
 * an animated countdown bar, and a seconds-remaining display.
 */
export const UndoToast = ({
  id,
  scenarioName,
  duration = 5000,
  onUndo,
  onExpire,
  onDismiss,
}: UndoToastProps) => {
  const durationSeconds = Math.ceil(duration / 1000);
  const [secondsLeft, setSecondsLeft] = useState(durationSeconds);
  const expirationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const handledRef = useRef(false);

  // Start the expiration timer and countdown interval
  useEffect(() => {
    expirationTimer.current = setTimeout(() => {
      if (!handledRef.current) {
        handledRef.current = true;
        onExpire(id);
      }
    }, duration);

    countdownInterval.current = setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      if (expirationTimer.current) clearTimeout(expirationTimer.current);
      if (countdownInterval.current) clearInterval(countdownInterval.current);
    };
  }, [id, duration, onExpire]);

  const clearTimers = useCallback(() => {
    if (expirationTimer.current) {
      clearTimeout(expirationTimer.current);
      expirationTimer.current = null;
    }
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
  }, []);

  const handleUndo = useCallback(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    clearTimers();
    onUndo(id);
  }, [id, onUndo, clearTimers]);

  const handleDismiss = useCallback(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    clearTimers();
    onDismiss(id);
  }, [id, onDismiss, clearTimers]);

  const durationCss = `${durationSeconds}s`;

  return (
    <div
      className="undo-toast undo-toast-enter"
      id={`undo-toast-${id}`}
      role="alert"
      aria-live="polite"
    >
      {/* Trash icon */}
      <span className="undo-toast-icon" aria-hidden="true">
        🗑️
      </span>

      {/* Message */}
      <span className="undo-toast-message">
        <span className="undo-toast-scenario-name">
          {"\u201c"}
          {scenarioName}
          {"\u201d"}
        </span>{" "}
        deleted
      </span>

      {/* Undo button */}
      <button
        type="button"
        className="undo-toast-btn"
        onClick={handleUndo}
        aria-label="Undo deletion"
      >
        Undo
      </button>

      {/* Dismiss button */}
      <button
        type="button"
        className="undo-toast-dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss and confirm deletion"
      >
        ×
      </button>

      {/* Countdown bar */}
      <div className="undo-toast-countdown-bar">
        <div
          className="undo-toast-countdown-fill"
          style={{ "--countdown-duration": durationCss } as React.CSSProperties}
        />
      </div>

      {/* Seconds remaining */}
      <span className="undo-toast-seconds">{secondsLeft}s</span>
    </div>
  );
};

/* ------------------------------------------------------------------ */

export interface UndoToastStackProps {
  /** Array of toast items to display */
  toasts: UndoToastItem[];
  /** Duration per toast in milliseconds (default: 5000) */
  duration?: number;
  /** Called when the user clicks Undo on a toast */
  onUndo: (id: string) => void;
  /** Called when a toast countdown expires */
  onExpire: (id: string) => void;
  /** Called when the user dismisses a toast */
  onDismiss: (id: string) => void;
}

const MAX_VISIBLE_TOASTS = 3;

/**
 * UndoToastStack — manages a vertical stack of up to 3 UndoToast instances.
 *
 * Renders toasts in reverse order (newest at bottom) with an opacity cascade
 * for stacked toasts. When more than 3 toasts are present, only the 3 most
 * recent are shown.
 */
export const UndoToastStack = ({
  toasts,
  duration = 5000,
  onUndo,
  onExpire,
  onDismiss,
}: UndoToastStackProps) => {
  if (toasts.length === 0) return null;

  // Show only the most recent toasts (up to MAX_VISIBLE_TOASTS)
  const visibleToasts = toasts.slice(-MAX_VISIBLE_TOASTS);

  return (
    <div className={cn("undo-toast-stack")} id="undo-toast-stack">
      {visibleToasts.map((toast) => (
        <UndoToast
          key={toast.id}
          id={toast.id}
          scenarioName={toast.scenarioName}
          duration={duration}
          onUndo={onUndo}
          onExpire={onExpire}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
};
